/**
 * @author William Alexander Livesley
 * @date    2018-01-30
 * @filename  index.tsx
 * @LastModifiedBy Ahmed Samer
 * @LastModified 2019-02-26
 * @Copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 */
import "regenerator-runtime/runtime";
import * as React from "react";
import ReactDOM from "react-dom";
import "../../node_modules/bootstrap/dist/js/bootstrap.min.js";
import { connect, Provider } from "react-redux";
import { ERROR_MESSAGES } from "./constants/errors-constants";
import { ACTION_MODAL_ID } from "./constants/general-constants";
import "./constants/social-platforms";
import { ACTION_MODAL_CONTEXT } from "./contexts/action-modal-context";
import { CognitoIdpAuthenticator } from "./react-components/authentication/cognito-idp-authenticator";
import { LinkedInAuthenticator } from "./react-components/authentication/linked-in-authenticator";
import { TwitterAuthenticator } from "./react-components/authentication/twitter-authenticator";
import { ActionModal, ActionModalProps } from "./react-components/modals/action-modal";
import { Modal } from "./react-components/modals/modal";
import HtmlPage from "./react-components/page/html-page";
import { Router } from "./utilities/router";
import { ErrorBoundary } from "./error-boundary";
import $ from "jquery";
import { LocalStorageKeys } from "./utilities/local-storage-keys";
import DetailsPanel from "./react-components/details-panel";
import { UserModals } from "./react-components/modals/user-modals";
import { AuthenticationModal } from "./react-components/modals/authentication";
import FullScreenVideo from "./react-components/full-screen-video";
import { ToastMessage } from "./react-components/page-framework/toast-message";
import { storageFactory } from "./utilities/local-storage-factory";
import { setProfileActiveTab, closeDetailsPanel, setAppState } from "./redux/actions";
import { ProfileTabsNames } from "./constants/profile-tabs-names";
import { store } from "./redux";
import isEmpty from "lodash.isempty";
import isEqual from "lodash.isequal";
import * as Actions from "./redux/actions";
import { AppState as StoreState } from "./redux";
import { FetchHtmlPageAction, FetchHtmlPagePayload } from "./redux/cache/types.js";
import { SetAppStatePayload, SetAppStateAction } from "./redux/app/types.js";
import { HtmlPageLoading } from "./react-components/page/html-page-loading";
import { PageLoadingShell } from "./react-components/page/page-loading-shell";
import { getBuiltInHtmlDocument } from "./redux/cache/static-routes";
import { loadCognitoClientModule } from "./authentication/cognito-module";
import { isPrerenderedRoute } from "./constants/prerender-routes";
const localStore = storageFactory(() => localStorage);
import debounce from "lodash.debounce";
import { syncTrackingWithStoredConsent } from "./utilities/tracking-consent";
import { PageMetaTags } from "./react-components/seo/page-meta";
import { getPageMeta, normalizePath } from "./constants/page-meta";
import { MuteProvider } from "./react-components/page-framework/add-to-saved-button/contexts/mute-context";
import { consentStore } from "./utilities/gtag-consent-store";
import { resolveViewerRegion } from "./utilities/viewer-region";
import { PageViewEvent } from "./utilities/gtag-analytics";
import { PAGE_TITLE_CONTEXT } from "./contexts/page-title-context";

/**
 * The prerendered page body, captured before React hydrates. The route's HtmlDocument is
 * fetched after boot (~1 s), so without this the first render emptied the prerendered page
 * and showed the loading shell: the whole page vanished and was rebuilt after it had
 * painted (CLS ~0.18 on mobile). App renders this markup unchanged until the real page is
 * ready. Only when the snapshot is of this URL.
 */
const PRERENDERED_PATH = normalizePrerenderPath(window.location.pathname);
const prerenderedMainHtml: string | null = (() => {
  // scripts/optimize-initial-load.js marks which route the snapshot was rendered for.
  // Production CloudFront serves index.html (the home snapshot) for every path, so the
  // URL alone does not say whether the markup on screen is this page.
  const root = document.getElementById("root");
  if (!root || root.getAttribute("data-prerendered-route") !== PRERENDERED_PATH) {
    return null;
  }
  const main = document.querySelector("#root .main-container");
  return main ? main.innerHTML : null;
})();

function normalizePrerenderPath(path: string): string {
  return path.replace(/\/$/, "") || "/";
}

const PrerenderedPage = ({ html }: { html: string }) => (
  // dangerouslySetInnerHTML makes hydrate keep the existing DOM instead of diffing it.
  <div className="main-container" dangerouslySetInnerHTML={{ __html: html }} suppressHydrationWarning={true} />
);

// import * as Sentry from "@sentry/react";
// import { Integrations } from "@sentry/tracing";

/**
 *
 *
 * @interface AppState
 */
interface AppState {
  /**
   *
   *
   * @type {ActionModalProps}
   * @memberof AppState
   */
  actionModalProps: ActionModalProps;

  /**
   *
   *
   * @type {React.ReactNode}
   * @memberof AppState
   */
  view: React.ReactNode;

  /**
   * The route path the head tags and page-view title are derived from.
   *
   * @type {string}
   * @memberof AppState
   */
  pathname: string;
}

interface MapStateProps {
  cache: { [s: string]: any };
  inProgress: { [s: string]: boolean };
  device: string;
}

interface MapDispatchProps {
  fetchHtmlPage: (payload: FetchHtmlPagePayload) => FetchHtmlPageAction;
  setAppState: (payload: SetAppStatePayload) => SetAppStateAction;
}

export type Props = MapDispatchProps & MapStateProps;

class App extends React.Component<Props, AppState> {
  private currentPageHasAsyncTitle = false;
  private currentPageKey: string | null = null;
  private currentPageAlias: string | null = null;
  private lastTrackedPageKey: string | null = null;
  private currentPageTitle: string | null = null;
  private pendingPageViewKey: string | null = null;

  ASYNC_TITLE_COMPONENT_KEYS = ["PersonDetails", "CompanyDetails"];
  DEFAULT_PAGE_VIEW_FALLBACK_MS = 500;
  ASYNC_TITLE_PAGEVIEW_FALLBACK_MS = 5000;
  // Fires PageViewEvent for the page currently pending, if any, and marks it tracked.
  // Called both by setPageTitle (fast path, real title) and the fallback timeout (pages
  // with no title-setting component).
  private sendPendingPageView = () => {
    if (this.pendingPageViewKey === null) {
      return;
    }
    this.pendingPageViewKey = null;
    this.lastTrackedPageKey = this.currentPageKey;
    new PageViewEvent(this.currentPageAlias, this.currentPageTitle).send();
  };

  /**
   * Re-reads the route from the address bar and pushes it into state so the head tags
   * re-render, and seeds the page-view title from the static route metadata.
   *
   * Pages whose title is built from fetched data (person, company) have no static entry.
   * Their title is left null rather than defaulted to the homepage copy, so that if the
   * fallback timer fires before the profile resolves the page view reports no title
   * instead of a title belonging to a different page.
   */
  private syncRouteMeta = () => {
    const pathname = normalizePath(window.location.pathname);
    const meta = getPageMeta(pathname);
    this.currentPageTitle = meta ? meta.title : null;
    if (this.state.pathname !== pathname) {
      this.setState({ pathname });
    }
  };

  private setPageTitle = (title: string) => {
    this.currentPageTitle = title;
    if (this.pendingPageViewKey === this.currentPageKey) {
      this.sendPendingPageView();
    }
  };

  constructor(props: any) {
    super(props);

    this.state = {
      actionModalProps: {
        allowCancelButton: false,
        buttonAction: null,
        buttonText: null,
        modalMessageBody: ERROR_MESSAGES.RE_SIGN_IN_MESSAGE,
        modalTitle: "Please sign in again",
      },

      view: prerenderedMainHtml ? <PrerenderedPage html={prerenderedMainHtml} /> : <div />,
      pathname: normalizePath(window.location.pathname),
    };

    this.showModal = this.showModal.bind(this);
    this.onUrlChange = this.onUrlChange.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  componentDidMount() {
    const currentHash = window.location.hash.replace("#", "");
    if (!isEmpty(currentHash)) {
      store.dispatch(setAppState({ currentHash }));
    }
    if (!this.props.device) {
      this.onResize();
    }
    // resolveViewerRegion() falls back to an unknown region (default consent policy)
    // until scripts/attach-cf-viewer-region.sh has been run against the distribution.
    resolveViewerRegion().then(({ country, subdivision }) => {
      consentStore.initialize(country, subdivision);
      syncTrackingWithStoredConsent();
    });

    window.addEventListener("rv-enter", this.onUrlChange, true);
    window.addEventListener("scroll", debounce(this.onScroll, 300), true);
    window.addEventListener("resize", debounce(this.onResize, 300), true);

    window.onhashchange = (event) => {
      this.onUrlChange(event);
    };
    window.onpopstate = (event) => {
      this.onUrlChange(event);
    };

    this.syncRouteMeta();
    this.getElementToRender().then((node: React.ReactNode): void => {
      const isLoadingPlaceholder = React.isValidElement(node) && node.type === PageLoadingShell;
      if (!(isLoadingPlaceholder && this.isShowingPrerenderedPage())) {
        this.setState({ view: node });
      }
      if (!isLoadingPlaceholder && this.currentPageKey !== this.lastTrackedPageKey) {
        this.pendingPageViewKey = this.currentPageKey;
        const fallbackDelay = this.currentPageHasAsyncTitle
          ? this.ASYNC_TITLE_PAGEVIEW_FALLBACK_MS
          : this.DEFAULT_PAGE_VIEW_FALLBACK_MS;
        setTimeout(this.sendPendingPageView, fallbackDelay);
      }
    });
  }

  componentWillUnmount() {
    window.removeEventListener("rv-enter", this.onUrlChange);
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onResize);
  }

  /** True while the prerendered markup is on screen and the URL is still the one it shows. */
  isShowingPrerenderedPage(): boolean {
    const view = this.state.view;
    return (
      React.isValidElement(view) &&
      view.type === PrerenderedPage &&
      normalizePrerenderPath(window.location.pathname) === PRERENDERED_PATH
    );
  }

  onScroll() {
    const pageYOffset = window.pageYOffset;
    store.dispatch(Actions.setPageScroll({ pageYOffset }));
  }

  onUrlChange() {
    const currentHash = window.location.hash.replace("#", "");
    const currentCtx = Router.getParameterByName("ctx");

    const state = store.getState();
    if (isEmpty(currentHash) && isEmpty(currentCtx)) {
      store.dispatch(closeDetailsPanel());
    }
    if (!isEmpty(currentHash)) {
      store.dispatch(setAppState({ currentHash }));
    }
    const profileActiveTab = state.account.profileActiveTab;
    if (ProfileTabsNames.includes(currentHash) && profileActiveTab !== currentHash) {
      store.dispatch(setProfileActiveTab({ activeTab: currentHash }));
    }
    this.syncRouteMeta();
    this.getElementToRender().then((node: React.ReactNode): void => {
      const isLoadingPlaceholder = React.isValidElement(node) && node.type === PageLoadingShell;
      if (!(isLoadingPlaceholder && this.isShowingPrerenderedPage())) {
        this.setState({ view: node });
      }
      if (!isLoadingPlaceholder && this.currentPageKey !== this.lastTrackedPageKey) {
        this.pendingPageViewKey = this.currentPageKey;
        const fallbackDelay = this.currentPageHasAsyncTitle
          ? this.ASYNC_TITLE_PAGEVIEW_FALLBACK_MS
          : this.DEFAULT_PAGE_VIEW_FALLBACK_MS;
        setTimeout(this.sendPendingPageView, fallbackDelay);
      }
    });
  }

  onResize() {
    const device =
      window.innerWidth > 1919
        ? "large"
        : window.innerWidth > 992
        ? "desktop"
        : window.innerWidth > 768
        ? "tablet"
        : "mobile";
    store.dispatch(Actions.setDevice({ device }));
  }

  componentDidUpdate(nextProps: Props) {
    if (nextProps.cache !== this.props.cache) {
      this.onUrlChange();
    }
  }

  showModal(modalProps: ActionModalProps) {
    if (!isEqual(modalProps.modalMessageBody, this.state.actionModalProps.modalMessageBody)) {
      this.setState({ actionModalProps: modalProps }, () => {
        $(`#${ACTION_MODAL_ID}`).modal({
          keyboard: false,
          backdrop: "static",
          show: true,
        });
      });
    }
  }

  /**
   *
   *
   * @returns {({
   *     userIdentityId: string;
   *     provider: "linkedin" | "twitter";
   *   })}
   * @memberof App
   */
  getUserIdentity(): {
    userIdentityId: string;
    provider: "linkedin" | "twitter";
  } {
    const userIdentity: {
      userIdentityId: string;
      provider: "linkedin" | "twitter";
    } = {
      userIdentityId: "",
      provider: "twitter",
    };

    userIdentity.userIdentityId = localStore.getItem(LocalStorageKeys.KEY_LOCAL_STORAGE_COGNITO_TWITTER_ID_CREDENTIALS);

    if (userIdentity.userIdentityId === undefined || userIdentity.userIdentityId === null) {
      userIdentity.userIdentityId = localStore.getItem(
        LocalStorageKeys.KEY_LOCAL_STORAGE_COGNITO_LINKEDIN_ID_CREDENTIALS,
      );
      userIdentity.provider = "linkedin";
    }
    return userIdentity;
  }

  /**
   *Function responsible to handle url query parameters
   *
   * @returns {React.ReactNode}
   * @memberof App
   */
  handleQueryStringUrl(): Promise<React.ReactNode> {
    let query: URLSearchParams;
    let twitterToken: string;
    let twitterVerifier: string;
    const userIdentity: {
      userIdentityId: string;
      provider: "linkedin" | "twitter";
    } = this.getUserIdentity();
    const userIdentityId: string = userIdentity.userIdentityId;
    let linkedInCode: string;
    let linkedInState: string;
    let queryParameters: string[];
    let pageAlias: string;
    let result: Promise<React.ReactNode>;

    // initialize query string handler
    query = new URLSearchParams(window.location.search);
    // attempt to extract query parameters to identify linked in sign in
    linkedInCode = query.get("code");
    linkedInState = query.get("state");

    // attempt to extract query parameters to identify twitter sign in
    twitterToken = query.get("oauth_token");
    twitterVerifier = query.get("oauth_verifier");

    // validate each sign in case
    if (linkedInCode !== null && linkedInCode !== undefined && linkedInState !== null && linkedInState !== undefined) {
      result = Promise.resolve(<LinkedInAuthenticator code={linkedInCode} />);
    } else if (
      twitterToken !== null &&
      twitterToken !== undefined &&
      twitterVerifier !== null &&
      twitterVerifier !== undefined
    ) {
      result = Promise.resolve(<TwitterAuthenticator oauthToken={twitterToken} oauthVerifier={twitterVerifier} />);
    }
    // other the two third party sign in we handle query parameter as router parameter
    else {
      // attempt to extract query parameters from
      result = Promise.all([Router.getQueryParameters(), Router.getAlias()]).then((res) => {
        queryParameters = res[0];
        pageAlias = res[1];

        const cacheKey = `${pageAlias}-${queryParameters.join()}`;
        this.currentPageKey = cacheKey;
        this.currentPageAlias = pageAlias;
        const cache = this.props.cache;
        const cachedAlias = getBuiltInHtmlDocument(cacheKey);

        const inProgress = this.props.inProgress[cacheKey];

        const variables = {
          userIdentityId,
          queryParameters,
          alias: pageAlias,
        };
        const htmlDocument = cache[cacheKey] || cachedAlias;
        const panels = htmlDocument?.urlRouteByAlias?.node?.panels;
        this.currentPageHasAsyncTitle =
          Array.isArray(panels) &&
          panels.some((panel) => this.ASYNC_TITLE_COMPONENT_KEYS.includes(panel.component?.key));
        if (isEmpty(htmlDocument) && !inProgress) {
          this.props.fetchHtmlPage({ variables, cacheKey });
        }

        let node;
        node = htmlDocument ? <HtmlPage htmlDocument={htmlDocument} cacheKey={cacheKey} /> : <PageLoadingShell />;
        return Promise.resolve(node);
      });
    }
    return result;
  }

  /**
   * Function responsible to handle which element should be rendered
   *
   * @memberof App
   */
  getElementToRender(): Promise<React.ReactNode> {
    const idpTokens: string = window.location.hash ? window.location.hash : "";
    let idpParameters: any;
    let isAccessToken: boolean;
    let result: Promise<React.ReactNode>;

    // apply regular expression to determine if access token matches
    isAccessToken = /access_token/i.test(idpTokens);
    if (isAccessToken) {
      idpParameters = {};
      idpTokens
        .replace("#", "")
        .split("&")
        .forEach((value: string): any => {
          const vl = value.split("=");
          idpParameters[vl[0]] = vl[1];
        });
      result = Promise.resolve(<CognitoIdpAuthenticator tokens={idpParameters} />);
    } else {
      result = this.handleQueryStringUrl();
    }
    return result;
  }

  render(): React.ReactNode {
    return (
      <div>
        <PageMetaTags pathname={this.state.pathname} />
        <HtmlPageLoading />
        <DetailsPanel />
        <UserModals />
        <MuteProvider>
          <PAGE_TITLE_CONTEXT.Provider value={{ setPageTitle: this.setPageTitle }}>
            {this.state.view}
          </PAGE_TITLE_CONTEXT.Provider>

          <ACTION_MODAL_CONTEXT.Provider
            value={{
              showModal: this.showModal,
            }}
          >
            <Modal
              id={ACTION_MODAL_ID}
              className="modal-dialog-centered modal-sm"
              title={this.state.actionModalProps.modalTitle}
            >
              <ActionModal {...this.state.actionModalProps} />
            </Modal>
          </ACTION_MODAL_CONTEXT.Provider>
        </MuteProvider>

        <FullScreenVideo />
        <ToastMessage />
      </div>
    );
  }
}

const mapState = (state: StoreState) => ({
  cache: state.cache.store,
  device: state.app.device,
  inProgress: state.cache.inProgress,
});

const mapDispatch = {
  fetchHtmlPage: Actions.fetchHtmlPage,
  setAppState: Actions.setAppState,
};

const ConnectedApp = connect(mapState, mapDispatch)(App);
// ReactDOM.render(
//  <ErrorBoundary>
//    <Provider store={store}>
//      <ConnectedApp />
//    </Provider>
//  </ErrorBoundary>,
//  document.getElementById("root"),
// );

const RootApp = (
  <ErrorBoundary>
    <Provider store={store}>
      <AuthenticationModal />
      <ConnectedApp />
    </Provider>
  </ErrorBoundary>
);

const rootElement = document.getElementById("root");

// Prerender snapshots are saved as index.html (/) or {route}.html. CloudFront rewrites
// extensionless paths to those files; hydrate wherever prerender markup is served.
const pathname = window.location.pathname.replace(/\/$/, "") || "/";
const shouldHydratePrerender = isPrerenderedRoute(pathname);

function bootApp() {
  if (rootElement.hasChildNodes() && shouldHydratePrerender) {
    ReactDOM.hydrate(RootApp, rootElement);
  } else {
    ReactDOM.render(RootApp, rootElement);
  }
}

// Load auth chunk before React mounts so Relay/GraphQL never races an empty module.
loadCognitoClientModule()
  .then(() => bootApp())
  .catch((err) => {
    console.error("[boot] cognito-client failed to load", err);
    bootApp();
  });
